/**
 * Status, filter, and display config for sport clubs.
 */
import { Trophy, Dumbbell, Waves, Bike, Zap, Shield, CircleDot, Wind } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { BadgeVariant } from '@/components/common/LabelBadge';

/** Badge + icon config for a sport type. */
export interface SportClubTypeConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor: string;
}

/** Generic sport-club entity badge. */
export const SPORT_CLUB_TYPE_CONFIG: SportClubTypeConfig = {
  label: 'Club de sport',
  icon: Trophy,
  badgeVariant: 'ghost',
  badgeColor: 'var(--color-3-nuance-1)',
};

/** Badge config for each sport type (keys = DB values). */
const SPORT_TYPE_CONFIG: Record<string, SportClubTypeConfig> = {
  football: {
    label: 'Football',
    icon: CircleDot,
    badgeVariant: 'ghost',
    badgeColor: 'var(--color-3-nuance-1)',
  },
  handball: {
    label: 'Handball',
    icon: CircleDot,
    badgeVariant: 'ghost',
    badgeColor: 'var(--color-1-nuance-1)',
  },
  basketball: {
    label: 'Basketball',
    icon: CircleDot,
    badgeVariant: 'ghost',
    badgeColor: 'rgb(var(--accent-orange))',
  },
  volleyball: {
    label: 'Volleyball',
    icon: CircleDot,
    badgeVariant: 'ghost',
    badgeColor: 'var(--color-2-nuance-2)',
  },
  rugby: {
    label: 'Rugby',
    icon: Wind,
    badgeVariant: 'ghost',
    badgeColor: 'var(--color-3-nuance-2)',
  },
  swimming: {
    label: 'Natation',
    icon: Waves,
    badgeVariant: 'ghost',
    badgeColor: 'var(--color-2-nuance-1)',
  },
  cycling: {
    label: 'Cyclisme',
    icon: Bike,
    badgeVariant: 'ghost',
    badgeColor: 'var(--color-3-nuance-1)',
  },
  athletics: {
    label: 'Athlétisme',
    icon: Zap,
    badgeVariant: 'ghost',
    badgeColor: 'rgb(var(--accent-orange))',
  },
  combat_sports: {
    label: 'Sports de combat',
    icon: Shield,
    badgeVariant: 'ghost',
    badgeColor: 'rgb(var(--accent-purple))',
  },
  martial_arts: {
    label: 'Arts martiaux',
    icon: Shield,
    badgeVariant: 'ghost',
    badgeColor: 'var(--color-1-nuance-1)',
  },
  gymnastics: {
    label: 'Gymnastique',
    icon: Dumbbell,
    badgeVariant: 'ghost',
    badgeColor: 'var(--color-2-nuance-2)',
  },
  other: {
    label: 'Autre',
    icon: Trophy,
    badgeVariant: 'ghost',
    badgeColor: 'var(--color-3-nuance-1)',
  },
};

/** Ordered sport-type keys for form / filter options. */
const SPORT_TYPE_KEYS: string[] = Object.keys(SPORT_TYPE_CONFIG);

/**
 * Returns the config for a given sport type key.
 * Falls back to a generic config if the key is unknown.
 */
export function getSportTypeConfig(sportType: string | null | undefined): SportClubTypeConfig {
  if (!sportType) return SPORT_CLUB_TYPE_CONFIG;
  return (
    SPORT_TYPE_CONFIG[sportType] ?? {
      label: sportType,
      icon: Trophy,
      badgeVariant: 'ghost',
      badgeColor: 'var(--color-3-nuance-1)',
    }
  );
}

/** Interface-element name for sport clubs. */
export const SPORT_CLUBS_MENU_ELEMENT_ID = 'sport-clubs';

/** Interface-element IDs for sport-club row actions. */
export const SPORT_CLUB_ACTION_IDS = {
  create: 'action-create-club',
  view: 'action-view-club',
  edit: 'action-edit-club',
  delete: 'action-delete-club',
  exportExcel: 'action-export-excel-club',
} as const;

/** Filter element IDs for the sport-clubs {@link DataTable}. */
export const SPORT_CLUB_FILTER_IDS = {
  search: 'filter-search-club',
  city: 'filter-city-club',
  sportType: 'filter-sport-type',
} as const;

/** Filter group IDs for the sport-clubs {@link DataTable} configure panel. */
export type SportClubFilterGroupId =
  (typeof SPORT_CLUB_FILTER_IDS)[keyof typeof SPORT_CLUB_FILTER_IDS];

/** Configure-panel order for sport-club filter groups. */
export const ALL_SPORT_CLUB_FILTER_GROUP_IDS: SportClubFilterGroupId[] = [
  SPORT_CLUB_FILTER_IDS.search,
  SPORT_CLUB_FILTER_IDS.city,
  SPORT_CLUB_FILTER_IDS.sportType,
];

/** Human-readable label per filter group (configure panel). */
export const SPORT_CLUB_FILTER_GROUP_LABELS: Record<SportClubFilterGroupId, string> = {
  [SPORT_CLUB_FILTER_IDS.search]: 'Recherche',
  [SPORT_CLUB_FILTER_IDS.city]: 'Ville',
  [SPORT_CLUB_FILTER_IDS.sportType]: 'Type de sport',
};

/** Sport-club list filter state. */
export interface SportClubFilters {
  search?: string;
  city?: string;
  sportType?: string;
}

/** Default sport-club list filters. */
export const DEFAULT_SPORT_CLUB_FILTERS: SportClubFilters = {
  search: undefined,
  city: undefined,
  sportType: undefined,
};

/** {@link Select} options for sport type. */
export const SPORT_TYPE_FORM_OPTIONS = SPORT_TYPE_KEYS.map((key) => ({
  value: key,
  label: SPORT_TYPE_CONFIG[key]?.label ?? key,
}));

/** Filter options for sport type, including "all". */
export const SPORT_TYPE_FILTER_OPTIONS = [
  { value: '', label: 'Tous les sports' },
  ...SPORT_TYPE_FORM_OPTIONS,
];

/** Row-level RBAC flags passed into the sport-club table. */
export type SportClubPageActions = {
  canView?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
  canExportExcel?: boolean;
};
